import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { User } from 'app/core/user/user.types';
import { SessionEntry } from './session-manager.types';

const AUTH_SCHEMA_VERSION = '2';

const STORAGE_KEYS = {
    SESSIONS: 'accountSessions',
    ACTIVE_ID: 'activeAccountId',
    TOKEN_PREFIX: 'session_',
    TOKEN_SUFFIX: '_token',
    USER_SUFFIX: '_user',
    PREFS_SUFFIX: '_prefs',
    // Legacy keys kept in sync for backward compatibility
    LEGACY_TOKEN: 'accessToken',
    LEGACY_USER: 'user',
    // Bump to force a clean sign-in for users on an older storage schema
    SCHEMA_VERSION: 'auth_schema_version',
};

/** Keys that are saved/restored per account when switching. */
const PER_ACCOUNT_PREF_KEYS = [
    'selected_advertiser',
    'super_admin_organization_id',
    'super_admin_organization_name',
];

@Injectable({ providedIn: 'root' })
export class SessionManagerService {
    private _sessions$ = new BehaviorSubject<SessionEntry[]>(this._readSessions());
    private _activeSession$ = new BehaviorSubject<SessionEntry | null>(this._resolveActive());

    get sessions$(): Observable<SessionEntry[]> {
        return this._sessions$.asObservable();
    }

    get activeSession$(): Observable<SessionEntry | null> {
        return this._activeSession$.asObservable();
    }

    // ── Read ──────────────────────────────────────────────────────────────

    getAll(): SessionEntry[] {
        return this._readSessions();
    }

    getActive(): SessionEntry | null {
        return this._resolveActive();
    }

    getActiveUserId(): number | null {
        const id = localStorage.getItem(STORAGE_KEYS.ACTIVE_ID);
        return id ? Number(id) : null;
    }

    getTokenForAccount(userId: number): string | null {
        return localStorage.getItem(this._tokenKey(userId));
    }

    getUserForAccount(userId: number): User | null {
        const raw = localStorage.getItem(this._userKey(userId));
        return raw ? JSON.parse(raw) : null;
    }

    hasMultipleSessions(): boolean {
        return this._readSessions().length > 1;
    }

    // ── Write ─────────────────────────────────────────────────────────────

    addOrUpdateSession(userId: number, token: string, user: User): void {
        const sessions = this._readSessions();
        const now = Date.now();

        const idx = sessions.findIndex((s) => s.userId === userId);
        const entry: SessionEntry = {
            userId,
            email: user.email,
            name: user.name,
            organizationName: user.organization?.name ?? '',
            organizationId: user.organization?.id ?? '',
            role: user.role,
            addedAt: idx >= 0 ? sessions[idx].addedAt : now,
            lastActiveAt: now,
        };

        if (idx >= 0) {
            sessions[idx] = entry;
        } else {
            sessions.push(entry);
        }

        // Persist per-account data
        localStorage.setItem(this._tokenKey(userId), token);
        localStorage.setItem(this._userKey(userId), JSON.stringify(user));
        localStorage.setItem(STORAGE_KEYS.SCHEMA_VERSION, AUTH_SCHEMA_VERSION);
        this._writeSessions(sessions);
    }

    /**
     * Kill switch: if the stored schema version is missing or older than
     * the current one, wipe ALL auth-related storage so the app forces a
     * clean sign-in. Must run BEFORE migrateFromLegacy().
     *
     * Returns true if the storage was wiped.
     */
    ensureSchemaVersion(): boolean {
        const stored = localStorage.getItem(STORAGE_KEYS.SCHEMA_VERSION);
        if (stored === AUTH_SCHEMA_VERSION) {
            return false;
        }

        const keysToRemove: string[] = [
            STORAGE_KEYS.LEGACY_TOKEN,
            STORAGE_KEYS.LEGACY_USER,
            STORAGE_KEYS.SESSIONS,
            STORAGE_KEYS.ACTIVE_ID,
        ];

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(STORAGE_KEYS.TOKEN_PREFIX)) {
                keysToRemove.push(key);
            }
        }

        for (const key of keysToRemove) {
            localStorage.removeItem(key);
        }

        for (const key of PER_ACCOUNT_PREF_KEYS) {
            localStorage.removeItem(key);
        }

        localStorage.setItem(STORAGE_KEYS.SCHEMA_VERSION, AUTH_SCHEMA_VERSION);
        return true;
    }

    /**
     * Make `userId` the active session.
     * Saves the current account's prefs, then restores the target account's prefs
     * and syncs legacy localStorage keys.
     */
    switchTo(userId: number): void {
        const sessions = this._readSessions();
        const target = sessions.find((s) => s.userId === userId);
        if (!target) {
            return;
        }

        // Save current account preferences before switching
        const currentId = this.getActiveUserId();
        if (currentId !== null && currentId !== userId) {
            this._savePrefs(currentId);
        }

        // Update last active timestamp
        target.lastActiveAt = Date.now();
        this._writeSessions(sessions);

        // Set the active pointer
        localStorage.setItem(STORAGE_KEYS.ACTIVE_ID, String(userId));

        // Sync legacy keys so the rest of the app can read them unchanged
        const token = this.getTokenForAccount(userId);
        const user = this.getUserForAccount(userId);

        if (token) {
            localStorage.setItem(STORAGE_KEYS.LEGACY_TOKEN, token);
        }
        if (user) {
            localStorage.setItem(STORAGE_KEYS.LEGACY_USER, JSON.stringify(user));
        }

        // Restore per-account preferences
        this._restorePrefs(userId);

        // Emit
        this._sessions$.next(this._readSessions());
        this._activeSession$.next(this._resolveActive());
    }

    removeSession(userId: number): void {
        let sessions = this._readSessions();
        sessions = sessions.filter((s) => s.userId !== userId);

        // Clean per-account storage
        localStorage.removeItem(this._tokenKey(userId));
        localStorage.removeItem(this._userKey(userId));
        localStorage.removeItem(this._prefsKey(userId));

        this._writeSessions(sessions);

        // If the removed session was the active one, switch to the most recent remaining
        const activeId = this.getActiveUserId();
        if (activeId === userId) {
            if (sessions.length > 0) {
                const mostRecent = sessions.reduce((a, b) =>
                    a.lastActiveAt > b.lastActiveAt ? a : b
                );
                this.switchTo(mostRecent.userId);
            } else {
                // No sessions left — clear everything
                this._clearAll();
            }
        }

        this._sessions$.next(this._readSessions());
        this._activeSession$.next(this._resolveActive());
    }

    removeAllSessions(): void {
        const sessions = this._readSessions();
        for (const s of sessions) {
            localStorage.removeItem(this._tokenKey(s.userId));
            localStorage.removeItem(this._userKey(s.userId));
            localStorage.removeItem(this._prefsKey(s.userId));
        }
        this._clearAll();
    }

    /**
     * One-time migration from single-key format to multi-session registry.
     * Idempotent — does nothing if the registry already exists.
     */
    migrateFromLegacy(): void {
        if (localStorage.getItem(STORAGE_KEYS.SESSIONS)) {
            return; // Already migrated
        }

        const token = localStorage.getItem(STORAGE_KEYS.LEGACY_TOKEN);
        const userRaw = localStorage.getItem(STORAGE_KEYS.LEGACY_USER);

        if (!token || !userRaw) {
            return; // Nothing to migrate
        }

        try {
            const user: User = JSON.parse(userRaw);
            const userId = Number(user.id);
            if (!userId) {
                return;
            }

            this.addOrUpdateSession(userId, token, user);
            this.switchTo(userId);
        } catch {
            // Corrupted data — skip migration
        }
    }

    // ── Private helpers ───────────────────────────────────────────────────

    private _tokenKey(userId: number): string {
        return `${STORAGE_KEYS.TOKEN_PREFIX}${userId}${STORAGE_KEYS.TOKEN_SUFFIX}`;
    }

    private _userKey(userId: number): string {
        return `${STORAGE_KEYS.TOKEN_PREFIX}${userId}${STORAGE_KEYS.USER_SUFFIX}`;
    }

    private _prefsKey(userId: number): string {
        return `${STORAGE_KEYS.TOKEN_PREFIX}${userId}${STORAGE_KEYS.PREFS_SUFFIX}`;
    }

    private _readSessions(): SessionEntry[] {
        const raw = localStorage.getItem(STORAGE_KEYS.SESSIONS);
        if (!raw) {
            return [];
        }
        try {
            return JSON.parse(raw);
        } catch {
            return [];
        }
    }

    private _writeSessions(sessions: SessionEntry[]): void {
        localStorage.setItem(STORAGE_KEYS.SESSIONS, JSON.stringify(sessions));
    }

    private _resolveActive(): SessionEntry | null {
        const id = this.getActiveUserId();
        if (id === null) {
            return null;
        }
        return this._readSessions().find((s) => s.userId === id) ?? null;
    }

    private _savePrefs(userId: number): void {
        const prefs: Record<string, string | null> = {};
        for (const key of PER_ACCOUNT_PREF_KEYS) {
            prefs[key] = localStorage.getItem(key);
        }
        localStorage.setItem(this._prefsKey(userId), JSON.stringify(prefs));
    }

    private _restorePrefs(userId: number): void {
        const raw = localStorage.getItem(this._prefsKey(userId));

        // Clear current prefs first
        for (const key of PER_ACCOUNT_PREF_KEYS) {
            localStorage.removeItem(key);
        }

        if (!raw) {
            return;
        }

        try {
            const prefs: Record<string, string | null> = JSON.parse(raw);
            for (const key of PER_ACCOUNT_PREF_KEYS) {
                if (prefs[key] !== null && prefs[key] !== undefined) {
                    localStorage.setItem(key, prefs[key]!);
                }
            }
        } catch {
            // Corrupted prefs — skip
        }
    }

    private _clearAll(): void {
        localStorage.removeItem(STORAGE_KEYS.SESSIONS);
        localStorage.removeItem(STORAGE_KEYS.ACTIVE_ID);
        localStorage.removeItem(STORAGE_KEYS.LEGACY_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.LEGACY_USER);
        for (const key of PER_ACCOUNT_PREF_KEYS) {
            localStorage.removeItem(key);
        }
        this._sessions$.next([]);
        this._activeSession$.next(null);
    }
}
